<template>
  <div class="h-full flex flex-col">
    <!-- Header -->
    <div class="p-4 border-b border-gray-200">
      <div class="flex items-center justify-between mb-2">
        <h3 class="text-lg font-semibold text-gray-900">Components</h3>
        <Button
          icon="pi pi-times"
          text
          class="text-gray-400 hover:text-gray-600"
          @click="$emit('close')"
        />
      </div>
      <div class="relative">
        <InputText
          v-model="searchQuery"
          placeholder="Search components..."
          class="w-full pl-8"
        />
        <i class="pi pi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
      </div>
    </div>
    
    <!-- Component Categories -->
    <div class="flex-1 overflow-y-auto p-4">
      <div v-for="category in filteredCategories" :key="category.name" class="mb-6">
        <h4 class="text-sm font-medium text-gray-700 mb-3 uppercase tracking-wide">
          {{ category.name }}
        </h4>
        
        <div class="grid grid-cols-1 gap-2">
          <div
            v-for="component in category.components"
            :key="component.type"
            class="component-item p-3 border border-gray-200 rounded-lg cursor-pointer hover:border-admin-primary hover:shadow-sm transition-all"
            :draggable="true"
            @click="handleComponentClick(component)"
            @dragstart="handleDragStart(component, $event)"
            @dragend="handleDragEnd"
          >
            <div class="flex items-start space-x-3">
              <div class="w-10 h-10 bg-admin-primary/10 rounded-lg flex items-center justify-center">
                <i :class="component.icon" class="text-admin-primary text-lg"></i>
              </div>
              
              <div class="flex-1 min-w-0">
                <h5 class="text-sm font-medium text-gray-900 mb-1">{{ component.name }}</h5>
                <p class="text-xs text-gray-600 mb-2">{{ component.description }}</p>
                
                <!-- Variants -->
                <div v-if="component.variants.length > 0" class="flex flex-wrap gap-1">
                  <span 
                    v-for="variant in component.variants.slice(0, 3)"
                    :key="variant"
                    class="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded"
                  >
                    {{ variant }}
                  </span>
                  <span 
                    v-if="component.variants.length > 3"
                    class="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded"
                  >
                    +{{ component.variants.length - 3 }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'

interface ComponentInfo {
  type: string
  name: string
  description: string
  icon: string
  category: string
  variants: string[]
  usageCount: number
}

const searchQuery = ref('')

// Component library data
const componentCategories = ref([
  {
    name: 'Page Components',
    components: [
      {
        type: 'cms.banner-section',
        name: 'Banner Section',
        description: 'Hero banners with images and CTAs',
        icon: 'pi pi-image',
        category: 'sections',
        variants: ['hero', 'cta', 'overlay-bottom'],
        usageCount: 45
      },
      {
        type: 'cms.text-section',
        name: 'Text Section',
        description: 'Rich text content with formatting',
        icon: 'pi pi-align-left',
        category: 'sections',
        variants: ['flow', 'highlight', 'structured'],
        usageCount: 32
      },
      {
        type: 'cms.card-section',
        name: 'Card Section',
        description: 'Cards with images, text, and links',
        icon: 'pi pi-th-large',
        category: 'sections',
        variants: ['standard', 'compact', 'overlay'],
        usageCount: 28
      },
      {
        type: 'cms.flex-section',
        name: 'Flex Section',
        description: 'Flexible layouts with child components',
        icon: 'pi pi-objects-column',
        category: 'sections',
        variants: ['two-column', 'feature-list', 'stats'],
        usageCount: 15
      },
      {
        type: 'cms.accordion-section',
        name: 'Accordion',
        description: 'Expandable content sections',
        icon: 'pi pi-list',
        category: 'sections',
        variants: ['default', 'bordered', 'filled'],
        usageCount: 12
      },
      {
        type: 'cms.contact-form',
        name: 'Contact Form',
        description: 'Contact forms with validation',
        icon: 'pi pi-envelope',
        category: 'sections',
        variants: ['simple', 'detailed', 'booking'],
        usageCount: 8
      }
    ]
  },
  {
    name: 'Text Elements',
    components: [
      {
        type: 'text.heading',
        name: 'Heading',
        description: 'Styled headings H1-H6',
        icon: 'pi pi-text',
        category: 'text',
        variants: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
        usageCount: 67
      },
      {
        type: 'text.paragraph',
        name: 'Paragraph',
        description: 'Formatted text paragraphs',
        icon: 'pi pi-align-justify',
        category: 'text',
        variants: ['normal', 'lead', 'small'],
        usageCount: 52
      },
      {
        type: 'text.list',
        name: 'List',
        description: 'Bulleted or numbered lists',
        icon: 'pi pi-list',
        category: 'text',
        variants: ['bulleted', 'numbered', 'checked'],
        usageCount: 18
      }
    ]
  },
  {
    name: 'Media',
    components: [
      {
        type: 'cms.image-gallery',
        name: 'Image Gallery',
        description: 'Photo galleries with lightbox',
        icon: 'pi pi-images',
        category: 'media',
        variants: ['grid', 'masonry', 'carousel'],
        usageCount: 22
      }
    ]
  }
])

const filteredCategories = computed(() => {
  if (!searchQuery.value) return componentCategories.value
  
  return componentCategories.value.map(category => ({
    ...category,
    components: category.components.filter(component => 
      component.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      component.description.toLowerCase().includes(searchQuery.value.toLowerCase())
    )
  })).filter(category => category.components.length > 0)
})

const emit = defineEmits<{
  'drag-start': [component: ComponentInfo]
  'select': [component: ComponentInfo]
  close: []
}>()

const handleComponentClick = (component: ComponentInfo) => {
  emit('select', component)
}

const handleDragStart = (component: ComponentInfo, event: DragEvent) => {
  if (event.dataTransfer) {
    event.dataTransfer.setData('application/json', JSON.stringify({
      type: component.type,
      name: component.name
    }))
    event.dataTransfer.effectAllowed = 'copy'
  }
  
  // Add visual feedback
  const target = event.target as HTMLElement
  target.classList.add('opacity-50')
}

const handleDragEnd = (event: DragEvent) => {
  const target = event.target as HTMLElement
  target.classList.remove('opacity-50')
}
</script>

<style scoped>
.component-item:active {
  cursor: grabbing;
}
</style>